import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookmarkPlus,
  Check,
  Library,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from "lucide-react";
import { createSavedQuestion } from "@/lib/api";
import { fetchCapabilities, refreshExamples, type Capabilities, type Examples } from "@/lib/capabilities";
import { relativeTime } from "@/lib/format";
import { bindPrompt } from "./bindPrompt";
import {
  ALL_PROMPTS,
  CATEGORY_DESCRIPTIONS,
  CATEGORY_LABELS,
  PROMPT_CATEGORIES,
  groupByCategory,
  type LibraryPrompt,
  type PromptCategory,
} from "./library";

/**
 * True when at least one resolved slot came from the live content
 * store (isExample=false) rather than the static demo-corpus fallback.
 * Drives the header's provenance line — we only claim "your content
 * store" once something real has actually resolved.
 */
function hasLiveSlot(examples: Examples | undefined): boolean {
  if (!examples) return false;
  return (Object.keys(examples) as Array<keyof Examples>)
    .filter((k) => k !== "resolvedAt")
    .some((k) => examples[k]?.isExample === false);
}

/**
 * Modal dialog that surfaces the curated prompt library. Each entry can
 * be added to the user's Saved questions one-by-one, by category, or
 * the whole starter pack at once. Adds POST to {@code /saved-questions}
 * and invalidate the saved-list query so it refreshes.
 *
 * Design choices worth knowing:
 *
 * - **Portal-rendered** so it isn't constrained by the SavedQuestionsPage
 *   layout grid, and so it can sit on top of the tour overlay.
 * - **localStorage tracks "added" state** keyed by prompt id. Surviving
 *   restarts means the user doesn't re-see prompts they've already
 *   added, even if they later delete them from saved (we use the
 *   library entry's id, not the resulting SavedQuestion id, so the
 *   markers are stable across delete-and-re-add).
 * - **Search is client-side** — fast enough for ~70 entries.
 * - **Esc closes; backdrop click closes** — standard modal hygiene.
 */
export function PromptLibraryDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [activeCategory, setActiveCategory] = useState<PromptCategory>("starter");
  const [search, setSearch] = useState("");
  const [added, setAdded] = useState<Set<string>>(() => loadAdded());
  const [refreshing, setRefreshing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  const grouped = useMemo(groupByCategory, []);

  // Same query key/fetcher ChatPage.tsx uses — shares the cache so a
  // refresh triggered here is visible there too, and vice versa.
  const capabilitiesQuery = useQuery<Capabilities>({
    queryKey: ["capabilities"],
    queryFn: fetchCapabilities,
    staleTime: 5 * 60 * 1000,
    retry: false,
    enabled: open,
  });
  const examples = capabilitiesQuery.data?.examples;

  async function onRefreshSamples() {
    setRefreshing(true);
    try {
      await refreshExamples(); // POST; kicks off a fresh resolve server-side
      const before = examples?.resolvedAt;
      // The resolve runs async server-side — poll the GET a few times
      // rather than looping indefinitely.
      for (let i = 0; i < 3; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await capabilitiesQuery.refetch();
        if (data?.examples?.resolvedAt !== before) break;
      }
    } catch (err) {
      // Best-effort: a failed refresh just leaves the existing samples
      // in place. Log rather than surfacing an unhandled rejection.
      console.error("Failed to refresh prompt-library sample objects", err);
    } finally {
      setRefreshing(false);
    }
  }

  const provenanceLine =
    examples?.resolvedAt && hasLiveSlot(examples)
      ? `examples from your content store · resolved ${relativeTime(examples.resolvedAt)}`
      : "showing sample objects";

  const addOne = useMutation({
    mutationFn: async (p: LibraryPrompt) =>
      createSavedQuestion({ name: p.title, question: p.question }),
    onSuccess: (_, p) => {
      qc.invalidateQueries({ queryKey: ["saved-questions"] });
      setAdded((s) => {
        const next = new Set(s);
        next.add(p.id);
        persistAdded(next);
        return next;
      });
    },
  });

  const addMany = useMutation({
    mutationFn: async (prompts: LibraryPrompt[]) => {
      // Sequential — so the server-side store gets stable creation
      // order, and so a partial failure leaves a clean prefix instead
      // of a scattered set.
      for (const p of prompts) {
        if (added.has(p.id)) continue;
        await createSavedQuestion({ name: p.title, question: bindPrompt(p.question, examples).text });
      }
      return prompts.map((p) => p.id);
    },
    onSuccess: (ids) => {
      qc.invalidateQueries({ queryKey: ["saved-questions"] });
      setAdded((s) => {
        const next = new Set(s);
        ids.forEach((id) => next.add(id));
        persistAdded(next);
        return next;
      });
    },
  });

  // Esc to close. Capture phase so it beats other modal listeners.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Reset the search input on each open so leftover text doesn't
  // confuse the user when they re-open the library.
  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  if (!open) return null;

  const visible = filterPrompts(grouped[activeCategory], search);
  const allInCategory = grouped[activeCategory] ?? [];
  const remainingInCategory = allInCategory.filter((p) => !added.has(p.id));

  return createPortal(
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[1px]"
      role="dialog"
      aria-modal="true"
      aria-label="Prompt library"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="flex h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-900/5"
      >
        {/* Header */}
        <header className="flex items-center gap-3 border-b border-slate-200 bg-slate-50 px-5 py-3">
          <Library size={18} className="text-emerald-600" aria-hidden />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <div className="text-sm font-semibold text-slate-900">Prompt library</div>
              <span className="text-2xs text-slate-400">· {ALL_PROMPTS.length} prompts</span>
              <button
                type="button"
                onClick={onRefreshSamples}
                disabled={refreshing}
                aria-label="Refresh samples"
                title="Refresh the sample objects used in these prompts"
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-2xs font-medium text-slate-500 transition hover:bg-slate-200 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <RefreshCw size={11} className={refreshing ? "animate-spin" : undefined} aria-hidden />
                Refresh samples
              </button>
            </div>
            <div className="text-xs text-slate-500">
              Pre-built natural-language questions covering every Operate tool. One click adds to your Saved questions.
            </div>
            <div className="mt-0.5 text-2xs text-slate-400">{provenanceLine}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close library"
            className="rounded-md p-1.5 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800"
          >
            <X size={16} />
          </button>
        </header>

        {/* Search */}
        <div className="border-b border-slate-200 px-5 py-3">
          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              aria-hidden
            />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search prompts (title, body, tools)…"
              className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-9 pr-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
            />
          </div>
          {search && (
            <p className="mt-1.5 text-2xs text-slate-500">
              Searching across all {ALL_PROMPTS.length} prompts. Clear the box to filter by category.
            </p>
          )}
        </div>

        {/* Body — 2-column: category nav + prompt list */}
        <div className="flex flex-1 min-h-0">
          {!search && (
            <aside className="w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50/40 py-2">
              <ul className="space-y-0.5 px-2">
                {PROMPT_CATEGORIES.map((cat) => {
                  const count = grouped[cat]?.length ?? 0;
                  const addedCount = (grouped[cat] ?? []).filter((p) => added.has(p.id))
                    .length;
                  const isActive = cat === activeCategory;
                  return (
                    <li key={cat}>
                      <button
                        type="button"
                        onClick={() => setActiveCategory(cat)}
                        className={`flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-1.5 text-left text-xs font-medium transition ${
                          isActive
                            ? "bg-emerald-600 text-white shadow-sm"
                            : "text-slate-700 hover:bg-slate-200/60"
                        }`}
                      >
                        <span className="truncate">{CATEGORY_LABELS[cat]}</span>
                        <span
                          className={`shrink-0 text-[10px] tabular-nums ${
                            isActive ? "text-emerald-50" : "text-slate-500"
                          }`}
                        >
                          {addedCount}/{count}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </aside>
          )}

          <main className="flex-1 overflow-y-auto px-5 py-4">
            {!search && (
              <div className="mb-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-slate-900">
                    {CATEGORY_LABELS[activeCategory]}
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {CATEGORY_DESCRIPTIONS[activeCategory]}
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    disabled={remainingInCategory.length === 0 || addMany.isPending}
                    onClick={() => addMany.mutate(remainingInCategory)}
                    className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {addMany.isPending ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Plus size={12} />
                    )}
                    Add{" "}
                    {remainingInCategory.length === 0
                      ? "all"
                      : `${remainingInCategory.length} remaining`}
                  </button>
                </div>
              </div>
            )}

            <ul className="grid gap-2">
              {visible.length === 0 && (
                <li className="rounded-md border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-xs text-slate-500">
                  No prompts match.
                </li>
              )}
              {visible.map((p) => {
                const isAdded = added.has(p.id);
                const bound = bindPrompt(p.question, examples);
                return (
                  <li
                    key={p.id}
                    className={`rounded-md border p-3 transition ${
                      isAdded
                        ? "border-emerald-200 bg-emerald-50/40"
                        : "border-slate-200 bg-white hover:border-emerald-200"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-medium text-slate-900">{p.title}</span>
                          {p.starter && (
                            <Badge tone="emerald">
                              <Sparkles size={9} /> Starter
                            </Badge>
                          )}
                          {p.requiresAdmin && <Badge tone="indigo">Admin</Badge>}
                          {p.requiresLens && <Badge tone="violet">Lens</Badge>}
                          {p.requiresMarkLogic && <Badge tone="violet">MarkLogic</Badge>}
                          {p.requiresCognosSdk && <Badge tone="violet">Cognos SDK</Badge>}
                          {p.isWrite && <Badge tone="amber">Writes</Badge>}
                          {bound.usedFallback && <Badge tone="violet">Sample object</Badge>}
                        </div>
                        <p className="mt-1 text-xs leading-relaxed text-slate-700">
                          {bound.text}
                        </p>
                        <p className="mt-1 text-2xs text-slate-500">{p.description}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1">
                          {p.toolsUsed.map((t) => (
                            <span
                              key={t}
                              className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] text-slate-600"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      </div>
                      <button
                        type="button"
                        disabled={isAdded || addOne.isPending}
                        onClick={() => addOne.mutate({ ...p, question: bound.text })}
                        className={`inline-flex shrink-0 items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition ${
                          isAdded
                            ? "cursor-default border border-emerald-200 bg-emerald-100 text-emerald-700"
                            : "border border-slate-200 bg-white text-slate-700 hover:bg-emerald-50 hover:text-emerald-700"
                        }`}
                      >
                        {isAdded ? (
                          <>
                            <Check size={11} /> Added
                          </>
                        ) : (
                          <>
                            <BookmarkPlus size={11} /> Add
                          </>
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </main>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-5 py-3 text-2xs text-slate-500">
          <div>
            {added.size} of {ALL_PROMPTS.length} added · Everything you add lands in
            Saved questions and can be edited or deleted there.
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-100"
          >
            Done
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

const STORAGE_KEY = "datumcraft.operate.promptLibrary.added";

function loadAdded(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((x): x is string => typeof x === "string"));
  } catch {
    return new Set();
  }
}

function persistAdded(s: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(s)));
  } catch {
    /* fail open — Safari private mode etc. */
  }
}

function filterPrompts(prompts: LibraryPrompt[], search: string): LibraryPrompt[] {
  const trimmed = search.trim().toLowerCase();
  if (!trimmed) return prompts;
  // Cross-category search when there's a query.
  return ALL_PROMPTS.filter((p) => {
    const hay = (
      p.title +
      " " +
      p.question +
      " " +
      p.description +
      " " +
      p.toolsUsed.join(" ")
    ).toLowerCase();
    return hay.includes(trimmed);
  });
}

function Badge({
  tone,
  children,
}: {
  tone: "emerald" | "indigo" | "violet" | "amber";
  children: React.ReactNode;
}) {
  const cls =
    tone === "emerald"
      ? "bg-emerald-100 text-emerald-700 border-emerald-200"
      : tone === "indigo"
      ? "bg-indigo-100 text-indigo-700 border-indigo-200"
      : tone === "violet"
      ? "bg-violet-100 text-violet-700 border-violet-200"
      : "bg-amber-100 text-amber-700 border-amber-200";
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {children}
    </span>
  );
}
